/* skinfood-homepage.js
   Homepage interactions:
   - .plan-toggle on product cards (sub vs one-time) — swaps data-sub/data-one values
   - [data-tier-list] tier selector (used by the Bundle card + bundle pitch section)
   - [data-hero-atc] hero CTAs — AJAX add bundle to cart, open Dawn drawer
   - [data-atc] bundle-pitch ATC — AJAX add selected tier, open Dawn drawer
   - [data-compare] side-by-side image compare slider
*/

/* --- Shared cart-add helper ---
   POST /cart/add.js, render Dawn cart-drawer, fall back to permalink on
   failure. Mirrors the pattern in skinfood-pdp.js.
*/
function skinfoodAjaxAddToCart(opts) {
  /* opts: { trigger, variantId, quantity, sellingPlanId, fallbackHref } */
  var trigger = opts.trigger;
  if (!trigger) return;
  if (trigger.getAttribute('aria-disabled') === 'true') return;
  if (!opts.variantId) {
    /* Nothing wired up — fall through to whatever href is on the trigger. */
    return;
  }
  trigger.setAttribute('aria-disabled', 'true');
  trigger.classList.add('is-loading');

  var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  var fd = new FormData();
  fd.append('id', opts.variantId);
  fd.append('quantity', opts.quantity || 1);
  if (opts.sellingPlanId) fd.append('selling_plan', opts.sellingPlanId);
  if (cart && typeof cart.getSectionsToRender === 'function') {
    fd.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }).join(','));
    fd.append('sections_url', window.location.pathname);
    if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
  }

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
    body: fd
  })
    .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, status: r.status, body: j }; }); })
    .then(function (res) {
      if (!res.ok || (res.body && res.body.status)) {
        window.location = opts.fallbackHref || (window.routes && window.routes.cart_url) || '/cart';
        return;
      }
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(res.body);
        cart.classList.remove('is-empty');
      } else {
        window.location = (window.routes && window.routes.cart_url) || '/cart';
      }
    })
    .catch(function () {
      window.location = opts.fallbackHref || '/cart';
    })
    .finally(function () {
      trigger.removeAttribute('aria-disabled');
      trigger.classList.remove('is-loading');
    });
}

/* --- Hero CTAs — Add Bundle / Subscribe & Save --- */
(function () {
  var heroCfg = (window.SkinfoodHomepage && window.SkinfoodHomepage.heroBundle) || {};
  document.querySelectorAll('[data-hero-atc]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      var mode = btn.getAttribute('data-hero-atc'); /* 'one' | 'sub' */
      if (!heroCfg.variant) return; /* no bundle product configured — let href win */
      e.preventDefault();
      skinfoodAjaxAddToCart({
        trigger: btn,
        variantId: heroCfg.variant,
        quantity: 1,
        sellingPlanId: mode === 'sub' ? heroCfg.plan : null,
        fallbackHref: btn.getAttribute('href')
      });
    });
  });
})();

/* --- Hero mini-quote rotator ---
   Cross-fades between [data-quote-slide] siblings every 7s. No-ops with
   <2 slides or under prefers-reduced-motion. aria-hidden is toggled so
   screen readers only announce the active slide.
*/
(function () {
  var rotator = document.querySelector('[data-quote-rotator]');
  if (!rotator) return;
  var slides = rotator.querySelectorAll('[data-quote-slide]');
  if (slides.length < 2) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  var i = 0;
  setInterval(function () {
    var prev = slides[i];
    i = (i + 1) % slides.length;
    var next = slides[i];
    prev.classList.remove('is-active');
    prev.setAttribute('aria-hidden', 'true');
    next.classList.add('is-active');
    next.removeAttribute('aria-hidden');
  }, 7000);
})();

/* --- Product card: pack tiers + plan rows ---
   Mirrors the PDP pattern (assets/skinfood-pdp.js). Each card tracks its
   active tier + active plan independently. Tier click updates plan-row
   prices; plan click updates the ATC label/href. The ATC rebuilds the
   /cart permalink from the active tier's variant + active plan's selling
   plan on every change. */
(function () {
  function render(card) {
    var state = card._cardState;
    if (!state) return;

    /* Active tier element */
    var activeTier = card.querySelector('[data-card-tiers] .tier.is-active') ||
                     card.querySelector('[data-card-tiers] .tier');
    if (!activeTier) return;

    var subPrice = activeTier.getAttribute('data-sub-price') || '';
    var onePrice = activeTier.getAttribute('data-one-price') || '';
    var variantId = activeTier.getAttribute('data-variant') || '';
    var subPlan = activeTier.getAttribute('data-sub-plan') || '';

    /* Push tier prices into plan rows. Prices are pre-formatted by Liquid
       via `| money`, so the currency symbol + locale formatting are baked
       into the string already — no `$` prefix or reformatting here. */
    card.querySelectorAll('[data-card-plans] [data-plan-price]').forEach(function (el) {
      var which = el.getAttribute('data-plan-price');
      el.textContent = which === 'sub' ? subPrice : onePrice;
    });

    /* Pack savings vs single-bottle × N — only meaningful on multi-pack
       tiers. data-save is already a formatted money string (e.g. "$48");
       rendered as a small "Save X" pill; empty pill collapses via CSS. */
    var saveAmt = activeTier.getAttribute('data-save') || '';
    card.querySelectorAll('[data-card-save]').forEach(function (el) {
      el.textContent = saveAmt ? 'Save ' + saveAmt : '';
    });

    /* Reflect plan active state. */
    card.querySelectorAll('[data-card-plans] .plan-row').forEach(function (row) {
      var isActive = row.dataset.plan === state.plan;
      row.classList.toggle('is-active', isActive);
      var input = row.querySelector('input[type="radio"]');
      if (input) input.checked = isActive;
    });

    /* CTA: label includes the product name + pack size so the customer
       can confirm at a glance what they're about to add. Tier 1 omits
       the pack suffix (single bottle is the implicit baseline). */
    var cta = card.querySelector('[data-card-atc]');
    if (cta) {
      var productName = cta.getAttribute('data-product-name') || '';
      var tierKey = activeTier.getAttribute('data-tier');
      var packSuffix = '';
      if (tierKey === '2') packSuffix = ' 2-Pack';
      else if (tierKey === '3') packSuffix = ' 3-Pack';
      cta.textContent = productName
        ? ('Add ' + productName + packSuffix + ' to Cart')
        : ('Add' + packSuffix + ' to Cart');

      var url = '/cart/' + variantId + ':1';
      if (state.plan === 'sub' && subPlan) url += '?selling_plan=' + subPlan;
      cta.setAttribute('href', url);
      cta.setAttribute('data-variant', variantId);
      cta.setAttribute('data-plan', state.plan === 'sub' ? subPlan : '');
    }

    /* Price-note microcopy under CTA. */
    var note = card.querySelector('.card-price-note[data-sub], .card-price-note[data-one]');
    if (note) {
      var val = note.dataset[state.plan];
      if (val != null) note.textContent = val;
    }
  }

  document.querySelectorAll('.product-card').forEach(function (card) {
    if (!card.querySelector('[data-card-tiers]') && !card.querySelector('[data-card-plans]')) return;
    card._cardState = { plan: 'one' };

    /* Tier selection */
    card.querySelectorAll('[data-card-tiers] .tier').forEach(function (tier) {
      function activate() {
        if (tier.classList.contains('is-unavailable')) return;
        card.querySelectorAll('[data-card-tiers] .tier').forEach(function (t) {
          t.classList.remove('is-active');
        });
        tier.classList.add('is-active');
        render(card);
      }
      tier.addEventListener('click', activate);
      var input = tier.querySelector('input[type="radio"]');
      if (input) {
        input.addEventListener('change', function () { if (input.checked) activate(); });
      }
    });

    /* Plan selection */
    card.querySelectorAll('[data-card-plans] .plan-row').forEach(function (row) {
      function activate() {
        card._cardState.plan = row.dataset.plan;
        render(card);
      }
      row.addEventListener('click', activate);
      var input = row.querySelector('input[type="radio"]');
      if (input) {
        input.addEventListener('change', function () { if (input.checked) activate(); });
      }
    });

    /* Initial render so the plan rows + CTA reflect tier 1 / sub plan. */
    render(card);
  });
})();

/* --- Choice-triptych card CTAs — AJAX add to cart, no PDP nav --- */
(function () {
  document.querySelectorAll('.product-card [data-card-atc]').forEach(function (cta) {
    cta.addEventListener('click', function (e) {
      var variantId = cta.getAttribute('data-variant');
      if (!variantId) return; /* no variant wired up — let href win */
      var card = cta.closest('.product-card');
      var activeRow = card && card.querySelector('[data-card-plans] .plan-row.is-active');
      var mode = (activeRow && activeRow.dataset.plan) || 'sub';
      var planId = mode === 'sub' ? cta.getAttribute('data-plan') : null;
      e.preventDefault();
      skinfoodAjaxAddToCart({
        trigger: cta,
        variantId: variantId,
        quantity: 1,
        sellingPlanId: planId,
        fallbackHref: cta.getAttribute('href')
      });
    });
  });
})();

/* --- Tier selector + ATC permalink (bundle card) ---
   Each .tier element carries data-variant + data-sub-plan emitted by the
   section. The handler reads from the SELECTED tier so each tier can map
   to its own variant + selling plan group (1u/2u/3u/bulk). Quantity is
   always 1 — tier IS the variant, not a multiplier of one base variant.
*/
(function () {
  document.querySelectorAll('[data-tier-list]').forEach(function (list) {
    var group = list.dataset.tierList;
    var toggle = document.querySelector('[data-tier-group="' + group + '"]');
    var atc = document.querySelector('[data-atc="' + group + '"]');
    var tiers = list.querySelectorAll('.tier');
    var mode = 'sub';

    function render() {
      tiers.forEach(function (tier) {
        var price = tier.dataset[mode + 'Price'];
        var strike = tier.dataset[mode + 'Strike'];
        var subtxt = tier.dataset[mode + 'Sub'];
        var p = tier.querySelector('.price');
        var s = tier.querySelector('.strike');
        var sub = tier.querySelector('.tier-sub');
        if (p) p.textContent = price;
        if (s) {
          s.textContent = strike || '';
          s.style.visibility = strike ? 'visible' : 'hidden';
        }
        if (sub) sub.textContent = subtxt || '';
      });
      var selected = list.querySelector('.tier.selected');
      if (selected && atc) {
        atc.textContent = 'Add To Cart · ' + selected.dataset[mode + 'Price'];
        var variantId = selected.dataset.variant;
        var planId = selected.dataset.subPlan;
        if (variantId) {
          var url = '/cart/' + variantId + ':1';
          if (mode === 'sub' && planId) url += '?selling_plan=' + planId;
          atc.setAttribute('href', url);
        }
      }
    }

    tiers.forEach(function (tier) {
      tier.addEventListener('click', function () {
        tiers.forEach(function (t) { t.classList.remove('selected'); });
        tier.classList.add('selected');
        render();
      });
    });

    if (toggle) {
      toggle.addEventListener('click', function (e) {
        if (e.target.tagName !== 'BUTTON') return;
        mode = e.target.dataset.mode;
        toggle.querySelectorAll('button').forEach(function (b) {
          b.classList.toggle('active', b.dataset.mode === mode);
        });
        render();
      });
    }

    /* AJAX cart-add — intercept the ATC click. The href stays as a no-JS
       fallback (Shopify cart permalink set in render()), but the typical
       path is now AJAX into the Dawn cart drawer. */
    if (atc) {
      atc.addEventListener('click', function (e) {
        var selected = list.querySelector('.tier.selected') || tiers[0];
        var variantId = selected && selected.dataset.variant;
        if (!variantId) return;
        e.preventDefault();
        skinfoodAjaxAddToCart({
          trigger: atc,
          variantId: variantId,
          quantity: 1,
          sellingPlanId: mode === 'sub' ? (selected.dataset.subPlan || null) : null,
          fallbackHref: atc.getAttribute('href')
        });
      });
    }

    render();
  });
})();

/* --- Compare slider (purity / moat section) --- */
(function () {
  document.querySelectorAll('[data-compare]').forEach(function (el) {
    var clip = el.querySelector('.compare-img-clip');
    if (!clip) return;
    var clipImg = clip.querySelector('img');
    var divider = el.querySelector('.compare-divider');
    var range = el.querySelector('.compare-range');

    function sizeClipImg() {
      if (!clipImg) return;
      clipImg.style.width = el.offsetWidth + 'px';
      clipImg.style.height = el.offsetHeight + 'px';
      clipImg.style.objectFit = 'cover';
      clipImg.style.position = 'absolute';
      clipImg.style.top = '0';
      clipImg.style.left = '0';
    }
    sizeClipImg();
    window.addEventListener('resize', sizeClipImg);

    function set(v) {
      v = Math.max(0, Math.min(100, v));
      clip.style.width = v + '%';
      if (divider) divider.style.left = v + '%';
      if (range) range.value = v;
    }
    set(50);
    if (range) {
      range.addEventListener('input', function (e) { set(parseFloat(e.target.value)); });
    }

    function pointerSet(e) {
      var rect = el.getBoundingClientRect();
      var x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      set((x / rect.width) * 100);
    }
    var dragging = false;
    el.addEventListener('mousedown', function (e) { dragging = true; pointerSet(e); });
    window.addEventListener('mousemove', function (e) { if (dragging) pointerSet(e); });
    window.addEventListener('mouseup', function () { dragging = false; });
    el.addEventListener('touchstart', function (e) { pointerSet(e); }, { passive: true });
    el.addEventListener('touchmove', function (e) { pointerSet(e); }, { passive: true });
  });
})();

/* Hero video — maximally aggressive autoplay. iOS Safari/Chrome will silently
   reject autoplay under various conditions (Low Power Mode, data saver, slow
   network, race with other JS). Strategy: try at every signal we can hook,
   poll for the first 12s, force on first user interaction, and reload+retry
   on error. The cost is a few hundred bytes of JS + a wasted play() call or
   two when it would've worked anyway. Worth it for "the video always plays". */
(function () {
  var videos = document.querySelectorAll('video[data-hero-video]');
  if (!videos.length) return;

  videos.forEach(function (v) {
    v.muted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('autoplay', '');

    var attempts = 0;
    function attempt() {
      if (!v.paused) return;
      attempts++;
      try {
        var p = v.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      } catch (e) {}
    }

    // Try immediately + at every readiness milestone the browser fires.
    attempt();
    ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough', 'progress'].forEach(function (evt) {
      v.addEventListener(evt, attempt);
    });

    // On error, force a reload of the source and try again.
    v.addEventListener('error', function () {
      try { v.load(); attempt(); } catch (e) {}
    });

    // Poll every 600ms for the first 12s — covers the case where the browser
    // fires no further events but the video is just sitting paused.
    var pollCount = 0;
    var poll = setInterval(function () {
      pollCount++;
      if (!v.paused || pollCount > 20) { clearInterval(poll); return; }
      attempt();
    }, 600);

    // First user interaction unconditionally satisfies the browser's
    // user-gesture policy; play() inside a touch/click handler always works.
    var unlock = function () {
      attempt();
      ['touchstart', 'touchend', 'click', 'scroll', 'keydown'].forEach(function (e) {
        window.removeEventListener(e, unlock);
      });
    };
    ['touchstart', 'touchend', 'click', 'scroll', 'keydown'].forEach(function (e) {
      window.addEventListener(e, unlock, { passive: true, once: false });
    });

    // Resume when the tab refocuses.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) attempt();
    });
    window.addEventListener('pageshow', attempt);
  });
})();

/* Card videos — eager autoplay with retry. Sources load on parse (no lazy
   promotion). play() is retried on canplay because iOS Safari sometimes
   rejects the initial autoplay even with muted+playsinline. iOS Low Power
   Mode blocks autoplay entirely; the poster image carries the experience
   in that case (set via product.featured_image as the always-current source). */
(function () {
  var videos = document.querySelectorAll('video[data-card-video]');
  if (!videos.length) return;

  videos.forEach(function (v) {
    v.muted = true;
    var attempt = function () {
      var p = v.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    };
    attempt();
    v.addEventListener('canplay', attempt, { once: true });
    v.addEventListener('loadeddata', attempt, { once: true });
  });
})();